import React from 'react';
import { connect } from 'react-redux';
import { User } from 'oidc-client';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '../../store';
import { Row } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router';
import * as ReportStore from '../../store/Report';
import ReportView from './ReportView';
import NavMenuSharableLink from '../Common/NavMenuSharableLink';
import { appSettings } from "../../appSettings";
import { PowerBiObjectType } from '../../types/Common/PowerBiObjectType';
import { ExportFileType } from '../../types/Common/Enums/ExportFileType';


type ReportViewerProps = {
    dispatch: Dispatch;
    reportState: ReportStore.ReportState;
    user: User;
    actions: typeof ReportStore.actionCreators;
}
& RouteComponentProps<{reportId: string}>;

interface ReportViewerState {
    isLoading: boolean;
    hasAttemptedRedirect: boolean;
}

class ReportViewer extends React.Component<ReportViewerProps, ReportViewerState> {
    private reportViewChildRef: React.RefObject<ReportView>;
    public constructor(props: ReportViewerProps) {
        super(props);
        this.state = {
            isLoading: true,
            hasAttemptedRedirect: false,
        };
        this.reportViewChildRef = React.createRef();
    }
    
    public componentDidMount() {
        this.props.actions.unloadReportDetails();
        this.ensureDataFetched();
    }


    public componentDidUpdate(previousProps: ReportViewerProps, previousState: ReportViewerState) {
        // Re-fetch if route param changes
        if (previousProps.match.params.reportId !== this.props.match.params.reportId) {
            this.props.actions.unloadReportDetails();
        }
        this.ensureDataFetched();

        // Attempt redirect after load completes, avoiding repeated attempts
        if (
        !this.state.hasAttemptedRedirect &&
        !this.props.reportState.isLoadingDetails &&
        this.props.reportState.isLoadedDetails
        ) {
            this.tryRedirectToAlias();
        }
    }
    
    private isGuid(value: string): boolean {
        return /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/.test(value);
    }

    private tryRedirectToAlias() {
        const paramId = this.props.match.params.reportId;

        // Mark that we attempted redirect (prevents re-entry)
        this.setState({ hasAttemptedRedirect: true });

        // Only redirect when the param is a GUID
        if (!this.isGuid(paramId)) return;

        // Find report by GUID in state; minimal details should include aliasName
        const report = this.props.reportState.data.find(x => x.id === paramId);
        const alias = report?.aliasName?.trim();

        // If no alias or already at the alias, do nothing
        if (!alias || alias.toLowerCase() === paramId.toLowerCase()) return;

        // Prefer a clean canonical public route (adjust to your router/paths)
        // window.location.assign() adds to history; replace() avoids keeping the GUID
        const redirectUrl = `/${appSettings.basePath ? appSettings.basePath + "/" : ""}reports/viewreport/${encodeURIComponent(alias)}`;
        window.location.replace(redirectUrl);
    }

    private ensureDataFetched() {
        this.props.actions.requestReportMinimalDetails(this.props.match.params.reportId);
        
        if (this.state.isLoading && !this.props.reportState.isLoadingDetails && this.props.reportState.isLoadedDetails) {
            this.setState({isLoading: false});
        }
    }

    // Handle the NavMenu click for full screen and pass on the request to the child component
    onFullScreenClick? = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.reportViewChildRef?.current?.powerBiEmbedFullScreen();
    };

    showExportFileRequestModal = (exportFileType: ExportFileType) => {
        this.reportViewChildRef?.current?.exportFileRequest(exportFileType);
    }

    public render() {
        const paramId = this.props.match.params.reportId;
        const report = this.props.reportState.data.find(x => x.id === paramId || x.aliasName === paramId);

        return (
        <React.Fragment>
            <div id="detailsContainer">
            <NavMenuSharableLink
                powerBiObjectType={PowerBiObjectType.Report}
                documentTitle={report?.name}
                documentId={report?.id}
                onFullScreenClick={this.onFullScreenClick}
                showExportFileRequestModal={this.showExportFileRequestModal}
            />
            <Row>
                <ReportView
                ref={this.reportViewChildRef}
                reportId={paramId}
                {...this.props}
                />
            </Row>
            </div>
        </React.Fragment>
        );
    }

}

function mapStateToProps(state: AppState) {
    return {
        reportState: state.report,
        user: state.oidc.user,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => {
        return {
            dispatch: dispatch,
            actions: bindActionCreators(Object.assign(
                {},
                ReportStore.actionCreators
            ), dispatch )
        }
    },
)(ReportViewer as any);
